import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { RouterPath } from "../../assets/dictionary/RouterPath";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Check, User, CreditCard, ArrowLeft, SlidersHorizontal, Settings } from "lucide-react";
import axios from "axios";
import myAppConfig from "../../config";
import { toast } from "sonner";
import { trackEvent } from "../../lib/matomo";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { Label } from "../../components/ui/label";

// Helper to redirect user to Stripe Checkout
const redirectTo = (url) => {
  window.location.href = url;
};

export default function BillingModern() {

  const REQUEST_QUOTE_EMAIL = "contact@peppacorp.fr";
  const requestQuote = () => {
    window.location.href = `mailto:${REQUEST_QUOTE_EMAIL}?subject=Demande%20de%20devis&body=Bonjour,%0D%0A%0D%0AJe souhaite obtenir un devis personnalisé.%0D%0A%0D%0ACordialement.`;
  };


  const navigate = useNavigate();
  const [selectedPlan] = useState("subscription");
  const [isLoading, setIsLoading] = useState(false);
  const [isCgvDialogOpen, setIsCgvDialogOpen] = useState(false);
  const [isCgvAccepted, setIsCgvAccepted] = useState(false);
  const [pendingPlanId, setPendingPlanId] = useState(null);

  const plans = [
      {
      id: "subscription",
      name: "Abonnement",
      price: "49.99",
      credits: "2500",
      description: "Accès illimité",
      features: [
        "Accès illimité au chat de recherche",
        "Accès au copilote de financements (lecture et analyse de documents)",
        "5 projets surveillés avec la veille automatisée"
      ],
      subscription: true,
      best_value: true,
      icon: User,
      color: "bg-yellow-500"
    },
    {
      id: "quote",
      name: "Devis sur mesure",
      price: null,
      credits: null,
      description: "Solution personnalisée",
      features: [
        "Besoins spécifiques ou volumétrie élevée",
        "Collectivités, grandes structures, entreprises",
        "Organismes accompagnateurs",
        "Accompagnement et tarification adaptée",
      ],
      subscription: false,
      best_value: false,
      icon: SlidersHorizontal,
      color: "bg-purple-500"
    },

    //   id: "payment",
    //   name: "Pack de crédits",
    //   price: "9.99",
    //   credits: "1000",
    //   description: "Pour un usage ponctuel",
    //   features: [
    //     "Associations et petites structures",
    //     "Projets ponctuels ou appels à projets spécifiques",
    //     "Entreprises traitant quelques dossiers occasionnels",
    //   ],
    //   subscription: false,
    //   best_value: false,
    //   icon: Zap,
    //   color: "bg-purple-500"
    // },

  ];

  const handlePurchase = async (planId) => {
    setIsLoading(true);
    try {
      const { data } = await axios.post(
        myAppConfig.api.ENDPOINT + "/api/v1/payments/checkout-session",
        { plan_id: planId },
        { withCredentials: true }
      );
      if (data?.url) {
        try { trackEvent("billing", "checkout_session_created", planId); } catch (_) { }
        redirectTo(data.url);
      } else {
        toast.error("Paiement indisponible.");
        try { trackEvent("billing", "checkout_missing_url", planId); } catch (_) { }
      }
    } catch (e) {
      toast.error("Erreur lors de l'initialisation du paiement.");
      try { trackEvent("billing", "checkout_error", planId); } catch (_) { }
    } finally {
      setIsLoading(false);
    }
  };

  const openCgvDialogForPlan = (planId) => {
    setPendingPlanId(planId);
    setIsCgvAccepted(false);
    setIsCgvDialogOpen(true);
    try { trackEvent("billing", "cgv_dialog_opened", planId); } catch (_) { }
  };

  const confirmCgvAndProceed = async () => {
    if (!isCgvAccepted || !pendingPlanId) return;
    setIsCgvDialogOpen(false);
    try { trackEvent("billing", "cgv_accepted", pendingPlanId); } catch (_) { }
    await handlePurchase(pendingPlanId);
    setPendingPlanId(null);
  };

  const handleCustomerPortal = async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.post(
        myAppConfig.api.ENDPOINT + "/api/v1/payments/customer-portal",
        {},
        { withCredentials: true }
      );
      if (data?.url) {
        try { trackEvent("billing", "customer_portal_opened"); } catch (_) { }
        redirectTo(data.url);
      } else {
        toast.error("Impossible d'accéder au portail client.");
        try { trackEvent("billing", "customer_portal_missing_url"); } catch (_) { }
      }
    } catch (e) {
      toast.error("Erreur : Aucun abonnement en cours.");
      try { trackEvent("billing", "customer_portal_error"); } catch (_) { }
    } finally {
      setIsLoading(false);
    }
  };





  return (
    <div className="min-h-screen relative app-page-gradient">
      <div className="container mx-auto px-4 py-8">
        <div className="sticky top-4 z-10">
          <Button
            variant="ghost"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(RouterPath.CHAT))}
            className="text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
        </div>
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-foreground mb-4">
            Choisissez votre plan
          </h1>
          {/* <p className="text-xl text-foreground max-w-2xl mx-auto opacity-80">
            Crédits à la carte ou abonnement, pour gérer vos projets à votre rythme.
          </p> */}
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan) => {
            const IconComponent = plan.icon;
            return (
              <Card
                key={plan.id}
                className={`relative ${selectedPlan === plan.id
                  ? 'ring-2 ring-primary shadow-lg'
                  : ''
                  } ${plan.best_value ? 'border-primary' : ''}`}
              >
                {plan.best_value && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary text-primary-foreground">
                    Recommandé
                  </Badge>
                )}

                <CardHeader className="text-center pb-4">
                  <div className={`w-12 h-12 rounded-full ${plan.color} flex items-center justify-center mx-auto mb-4`}>
                    <IconComponent className="h-6 w-6 text-white" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-foreground">
                    {plan.name}
                  </CardTitle>
                  <CardDescription className="text-foreground opacity-80">
                    {plan.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  {/* Price */}
                  {/*<div className="text-center">
                    <div className="text-4xl font-bold text-foreground">
                      {plan.price === "0" ? "Gratuit" : `${plan.price}€HT`}
                    </div>
                    <div className="text-sm text-foreground opacity-70">
                      {plan.subscription ? "par mois" : ""}
                    </div>
                    <div className="text-lg font-semibold text-primary mt-2 inline-flex items-center gap-1">
                      {plan.credits}
                      <img src={creditsIcon} alt="crédits" className="inline-block w-4 h-4" />
                      {plan.subscription ? "/mois" : ""}
                    </div>
                  </div>*/}
                  {plan.price && (
                    <div className="text-center">
                      <div className="text-4xl font-bold text-foreground">
                        {`${plan.price}€HT`}
                      </div>
                      <div className="text-sm text-foreground opacity-70">
                        {plan.subscription ? "par structure / mois" : ""}
                      </div>
                      {/* <div className="text-lg font-semibold text-primary mt-2 inline-flex items-center gap-1">
                        {plan.credits}
                        <img src={creditsIcon} alt="crédits" className="inline-block w-4 h-4" />
                        {plan.subscription ? "/mois" : ""}
                      </div> */}
                    </div>
                  )}


                  {/* Features */}
                  <div className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span className="text-sm text-foreground">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  {/* <Button
                    className={`w-full ${plan.best_value
                        ? 'bg-primary hover:bg-primary/90'
                        : 'bg-secondary hover:bg-secondary/80'
                      }`}
                    onClick={() => openCgvDialogForPlan(plan.id)}
                    disabled={isLoading}
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    {plan.subscription ? "S'abonner" : "Achat unique"}
                  </Button> */}
                  {plan.id === "subscription" ? (
                    <Button
                      className="w-full bg-primary hover:bg-primary/90"
                      onClick={() => openCgvDialogForPlan(plan.id)}
                      disabled={isLoading}
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      S'abonner
                    </Button>
                  ) : (
                    <Button
                      className="w-full bg-secondary hover:bg-secondary/80"
                      onClick={requestQuote}
                    >
                      Demander un devis
                    </Button>
                  )}

                </CardContent>
              </Card>
            );
          })}
        </div>


        {/* Customer Portal Access */}
        <div className="mt-12 text-center">
          <Button
            className="bg-muted hover:bg-muted/80 text-foreground"
            onClick={handleCustomerPortal}
            disabled={isLoading}
          >
            <Settings className="h-4 w-4 mr-2" />
            Gérer mes abonnements
          </Button>
        </div>
      </div>

      {/* CGV Dialog */}
      <Dialog open={isCgvDialogOpen} onOpenChange={setIsCgvDialogOpen}>
        <DialogContent>
          <DialogHeader className="text-foreground">
            <DialogTitle>Conditions Générales de Vente</DialogTitle>
            <DialogDescription>
              Veuillez lire et accepter les CGV avant de continuer vers le paiement.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <a
              href="https://peppacorp.fr/cgv"
              target="_blank"
              rel="noreferrer"
              className="text-primary underline"
              onClick={() => { try { trackEvent("billing", "cgv_open_pdf", pendingPlanId); } catch (_) { } }}
            >
              Ouvrir les CGV
            </a>

            <div className="flex items-center gap-3">
              <Checkbox id="accept-cgv" checked={isCgvAccepted} onCheckedChange={(v) => setIsCgvAccepted(!!v)} />
              <Label htmlFor="accept-cgv" className="text-foreground">
                J’ai lu et j’accepte les CGV
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setIsCgvDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={confirmCgvAndProceed} disabled={!isCgvAccepted || isLoading}>
              Continuer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
